import {
  type PromptAttachment,
  type PromptSelection,
  type PromptSelectionAttachment,
  type PromptSelectionOrigin,
} from './kng-model.assistant';

export type WikiPageCatalogEntry = {
  file: string;
  id?: string;
  description?: string;
};

export type CurrentFileMeta = {
  content?: string;
  id?: string;
  kind?: string;
  file?: string;
};

function isSelectionAttachment(attachment: PromptAttachment): attachment is PromptSelectionAttachment {
  return attachment.type === 'selection';
}

function selectionOrigin(attachment: PromptSelectionAttachment): PromptSelectionOrigin {
  if (attachment.origin === 'editor' || attachment.origin === 'history') {
    return attachment.origin;
  }
  return attachment.from != null && attachment.to != null ? 'editor' : 'history';
}

function isEditorSelection(attachment: PromptAttachment): attachment is PromptSelectionAttachment {
  return isSelectionAttachment(attachment) && selectionOrigin(attachment) === 'editor';
}

export function editorSelection(attachments: PromptAttachment[]): PromptSelectionAttachment | undefined {
  return attachments.find(isEditorSelection);
}

export function historySelection(attachments: PromptAttachment[]): PromptSelectionAttachment | undefined {
  return attachments.find((attachment): attachment is PromptSelectionAttachment => (
    isSelectionAttachment(attachment) && selectionOrigin(attachment) === 'history'
  ));
}

export function attachmentKey(attachment: PromptAttachment): string {
  if (attachment.type === 'selection') {
    return `selection:${selectionOrigin(attachment)}`;
  }
  return `${attachment.type}:${attachment.id || attachment.data}`;
}

export function toPromptSelection(attachment: PromptSelectionAttachment): PromptSelection {
  return {
    type: 'history',
    text: attachment.text,
    ...(attachment.source ? { source: attachment.source } : {}),
    ...(attachment.messageId ? { messageId: attachment.messageId } : {}),
    ...(attachment.role ? { role: attachment.role } : {}),
  };
}

export function toSelectionAttachment(selection: PromptSelection): PromptSelectionAttachment {
  return {
    type: 'selection',
    origin: 'history',
    text: selection.text,
    ...(selection.source ? { source: selection.source } : {}),
    ...(selection.messageId ? { messageId: selection.messageId } : {}),
    ...(selection.role ? { role: selection.role } : {}),
  };
}

export function withEditorSelectionText(
  attachments: PromptAttachment[],
  text: string,
): PromptAttachment[] {
  const trimmed = text.trim();
  if (!trimmed) {
    return attachments;
  }
  return attachments.map((attachment) => (
    isEditorSelection(attachment) ? { ...attachment, text: trimmed } : attachment
  ));
}

function isWikiEditorRun(agent?: string | null): boolean {
  const name = String(agent || '');
  return name === 'Wiki-editor' || name.startsWith('Wiki-editor#');
}

export function wikiQueryExtras(
  agent: string | undefined,
  extras: { currentVault?: string; wikiPages?: WikiPageCatalogEntry[] },
): { currentVault?: string; wikiPages?: WikiPageCatalogEntry[] } {
  return isWikiEditorRun(agent) ? extras : {};
}

function escapeXmlAttribute(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function resolveSelectionXml(
  selection: PromptSelectionAttachment | PromptSelection | null | undefined,
): string {
  if (!selection?.text?.trim()) {
    return '';
  }
  const origin: PromptSelectionOrigin = 'origin' in selection
    ? selectionOrigin(selection)
    : 'history';
  const source = selection.source;
  const messageId = 'messageId' in selection ? selection.messageId : undefined;
  const role = 'role' in selection ? selection.role : undefined;
  const attributes = [
    `origin="${escapeXmlAttribute(origin)}"`,
    source ? `source="${escapeXmlAttribute(source)}"` : '',
    messageId ? `messageId="${escapeXmlAttribute(messageId)}"` : '',
    role ? `role="${escapeXmlAttribute(role)}"` : '',
  ].filter(Boolean).join(' ');
  return `<selection ${attributes}>\n${selection.text.trim()}\n</selection>`;
}

function wrapCurrentVault(id: string, inner?: string): string {
  const body = String(inner || '').trim();
  const name = escapeXmlAttribute(id);
  return body
    ? `<current_vault id="${name}">\n${body}\n</current_vault>`
    : `<current_vault id="${name}"></current_vault>`;
}

function formatVaultCatalog(pages?: WikiPageCatalogEntry[] | null): string {
  return (pages || [])
    .map((page) => {
      const file = String(page.file || '').replace(/\s+/g, ' ').trim();
      if (!file) return '';
      const id = String(page.id || file.replace(/\.md$/i, '')).replace(/\s+/g, ' ').trim();
      const description = String(page.description || '').replace(/\s+/g, ' ').replace(/\|/g, '/').trim();
      const line = [id, file, description].filter(Boolean).join(' | ');
      return `- ${line}`;
    })
    .filter(Boolean)
    .join('\n');
}

function resolveCurrentVaultXml(
  vault?: string | null,
  pages?: WikiPageCatalogEntry[] | null,
): string {
  const id = String(vault || '').trim();
  if (!id) return '';
  return wrapCurrentVault(id, formatVaultCatalog(pages));
}

function resolveCurrentFileXml(file?: CurrentFileMeta | string | null): string {
  const meta = typeof file === 'string' ? { content: file } : file;
  if (!meta) {
    return '';
  }
  const text = String(meta.content ?? '');
  const attributes = [
    meta.id ? `id="${escapeXmlAttribute(String(meta.id))}"` : '',
    meta.kind ? `kind="${escapeXmlAttribute(meta.kind)}"` : '',
    meta.file ? `file="${escapeXmlAttribute(meta.file)}"` : '',
  ].filter(Boolean).join(' ');
  if (!text.trim() && !attributes) {
    return '';
  }
  return attributes
    ? `<current_file ${attributes}>\n${text}\n</current_file>`
    : `<current_file>\n${text}\n</current_file>`;
}

/** Query nue + tags front. Les `<join>` restent côté serveur. */
export function buildPromptQuery(params: {
  text: string;
  currentFile?: CurrentFileMeta;
  currentVault?: string;
  wikiPages?: WikiPageCatalogEntry[];
  content?: string;
  attachments?: PromptAttachment[];
}): string {
  const parts: string[] = [];
  const text = String(params.text || '').trim();
  if (text) {
    parts.push(text);
  }
  const vault = resolveCurrentVaultXml(params.currentVault, params.wikiPages);
  if (vault) {
    parts.push(vault);
  }
  const page = resolveCurrentFileXml(
    params.currentFile
      ? { ...params.currentFile, content: params.currentFile.content ?? params.content }
      : params.content
        ? { content: params.content }
        : undefined,
  );
  if (page) {
    parts.push(page);
  }
  for (const attachment of params.attachments || []) {
    if (!isSelectionAttachment(attachment)) {
      continue;
    }
    const xml = resolveSelectionXml(attachment);
    if (xml) {
      parts.push(xml);
    }
  }
  return parts.join('\n\n');
}
